import React from 'react';
import type { ConfirmWidgetData, ConfirmWidgetKind } from '../../types/widgets';

const KIND_ACCENT: Record<ConfirmWidgetKind, { icon: string; accentColor: string; accentBg: string }> = {
  cancel: { icon: '🚫', accentColor: '#DC2626', accentBg: '#FEF2F2' },
  refund: { icon: '💰', accentColor: '#D97706', accentBg: '#FFFBEB' },
  return: { icon: '↩️', accentColor: '#7C3AED', accentBg: '#F5F3FF' },
};

export interface ConfirmWidgetProps {
  data: ConfirmWidgetData;
}

export const ConfirmWidget: React.FC<ConfirmWidgetProps> = ({ data }) => {
  const { kind, title, body, primaryLabel, secondaryLabel, lineItems, totalAmount } = data;
  const { icon, accentColor, accentBg } = KIND_ACCENT[kind] ?? KIND_ACCENT.cancel;

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 420,
        background: 'var(--voice-widget-card-bg, #FFFFFF)',
        border: `1.5px solid ${accentColor}33`,
        borderRadius: 16,
        padding: '18px 18px 16px',
        boxSizing: 'border-box',
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            background: accentBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: 'var(--voice-widget-fg, #111827)',
              lineHeight: 1.3,
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--voice-widget-muted, #6B7280)',
              marginTop: 3,
              lineHeight: 1.4,
            }}
          >
            {body}
          </div>
        </div>
      </div>

      {/* Line items */}
      {lineItems && lineItems.length > 0 && (
        <div
          style={{
            background: 'var(--voice-widget-img-bg, #F9FAFB)',
            borderRadius: 8,
            padding: '8px 12px',
            marginBottom: 12,
          }}
        >
          {lineItems.map((li, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 12,
                padding: '3px 0',
                borderBottom:
                  i < lineItems.length - 1 ? '1px solid var(--voice-widget-border, #E5E7EB)' : undefined,
              }}
            >
              <span style={{ color: 'var(--voice-widget-muted, #6B7280)' }}>{li.label}</span>
              <span style={{ fontWeight: 600, color: 'var(--voice-widget-fg, #111827)' }}>{li.value}</span>
            </div>
          ))}
          {totalAmount && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 13,
                fontWeight: 700,
                paddingTop: 6,
                marginTop: lineItems.length > 0 ? 4 : 0,
                borderTop: lineItems.length > 0 ? `1px solid ${accentColor}33` : undefined,
                color: accentColor,
              }}
            >
              <span>Total</span>
              <span>{totalAmount}</span>
            </div>
          )}
        </div>
      )}

      {/* Verbal-prompt labels — visual-only (user says yes/no aloud) */}
      <div style={{ display: 'flex', gap: 8 }}>
        <div
          style={{
            flex: 1,
            padding: '8px 0',
            borderRadius: 8,
            background: accentBg,
            border: `1px solid ${accentColor}44`,
            textAlign: 'center',
            fontSize: 12,
            fontWeight: 700,
            color: accentColor,
          }}
        >
          {primaryLabel}
        </div>
        <div
          style={{
            flex: 1,
            padding: '8px 0',
            borderRadius: 8,
            background: 'var(--voice-widget-img-bg, #F9FAFB)',
            border: '1px solid var(--voice-widget-border, #E5E7EB)',
            textAlign: 'center',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--voice-widget-muted, #6B7280)',
          }}
        >
          {secondaryLabel}
        </div>
      </div>
      <div
        style={{
          textAlign: 'center',
          fontSize: 11,
          color: 'var(--voice-widget-muted, #6B7280)',
          marginTop: 8,
        }}
      >
        Say your choice aloud
      </div>
    </div>
  );
};

export default ConfirmWidget;
